import { buttonStyle } from 'components/buttons/buttonStyle';
import { ErrorOutline as ErrorOutlineIcon, InfoOutlined as InfoOutlinedIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Tooltip } from 'components/ui/tooltip';
import { ReactNode, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { useGetDebriefingClientRollupQuery } from 'store/api/debriefings';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { scoreToneClasses } from './scoreTone';
import {
    DEBRIEFING_LESSON_CATEGORY_CLASSIFIER,
    DebriefingClientRollup,
    DebriefingRecurringTag,
    DebriefingScoreAverage,
} from './types';

/** All six categories, in the order the spec lists them; the overall excludes general performance. */
const SCORE_ROWS: { labelKey: string; field: keyof DebriefingClientRollup }[] = [
    { labelKey: 'debriefing.score-categories.scope', field: 'scoreScopeMgmt' },
    { labelKey: 'debriefing.score-categories.team', field: 'scoreTeamComposition' },
    { labelKey: 'debriefing.score-categories.deadlines', field: 'scoreDeadlinesMgmt' },
    { labelKey: 'debriefing.score-categories.budget', field: 'scoreBudgetMgmt' },
    { labelKey: 'debriefing.score-categories.communication', field: 'scoreCommunication' },
    { labelKey: 'debriefing.score-categories.general', field: 'scoreGeneralPerformance' },
];

const VISIBLE_PATTERN_ROWS = 5;

const CARD_CLASS_NAME =
    'border-divider bg-paper overflow-hidden rounded-[10px] border px-5 py-4.5 new:border-0 new:shadow-card new:outline-hidden';

// An app alert: a white banner whose glyph, not its fill, carries the severity. The glyph sits in a
// wrapper because the kit alert lays a direct svg out as a grid column and nudges it down.
const ALERT_CLASS_NAME =
    'bg-paper text-fg flex items-center gap-2 rounded-lg border-transparent py-2 pr-4 pl-3 font-semibold';

type PatternKind = 'issue' | 'strength';

interface PatternRow extends DebriefingRecurringTag {
    kind: PatternKind;
}

function ScorePill({ average, large = false }: { average: number; large?: boolean }) {
    return (
        <span
            className={cn(
                'rounded-sm px-2 py-0.5 font-bold whitespace-nowrap',
                large ? 'text-[13px]' : 'text-xs',
                scoreToneClasses(average)
            )}
        >
            {average.toFixed(1)}
        </span>
    );
}

function CardTitle({
    labelKey,
    subtitleKey,
    tooltipKey,
}: {
    labelKey: string;
    subtitleKey: string;
    tooltipKey: string;
}) {
    return (
        <>
            <p className="m-0 text-sm font-bold">{t(labelKey)}</p>
            <Tooltip content={t(tooltipKey)}>
                <p className="text-fg-secondary m-0 mb-3.5 w-fit cursor-help text-xs">{t(subtitleKey)}</p>
            </Tooltip>
        </>
    );
}

function SummaryRow({ label, tooltipKey, children }: { label: string; tooltipKey?: string; children: ReactNode }) {
    return (
        <div className="border-divider flex items-center justify-between gap-2.5 border-b py-3 first:pt-0 last:border-b-0 last:pb-0">
            <p className="m-0 flex items-center gap-1 text-[13px]">
                {label}
                {tooltipKey ? <InfoTooltip text={tooltipKey} /> : null}
            </p>
            {children}
        </div>
    );
}

function SummaryCard({ clientPartyId, rollup }: { clientPartyId: number; rollup: DebriefingClientRollup }) {
    return (
        <section className={CARD_CLASS_NAME}>
            <p className="m-0 mb-3.5 text-sm font-bold">{t('debriefing.rollup.summary-title')}</p>
            <div>
                <SummaryRow label={t('debriefing.rollup.debriefings-label')}>
                    <p className="m-0 text-[13px] font-semibold whitespace-nowrap">{rollup.debriefingCount}</p>
                </SummaryRow>
                <SummaryRow label={t('debriefing.rollup.overall-self-evaluation')}>
                    {rollup.overallRating.average != null ? (
                        <ScorePill average={rollup.overallRating.average} large />
                    ) : (
                        <p className="text-fg-secondary m-0 text-[13px]">&ndash;</p>
                    )}
                </SummaryRow>
                <SummaryRow
                    label={t('debriefing.rollup.connected-tasks')}
                    tooltipKey="debriefing.rollup.connected-tasks-tooltip"
                >
                    {rollup.taskTotalCount > 0 ? (
                        <RouterLink
                            to={`/contact/${clientPartyId}/tasks`}
                            className="entity-link text-fg-secondary w-fit text-[13px] font-normal whitespace-nowrap no-underline hover:underline"
                        >
                            <FormattedMessage
                                id="debriefing.rollup.n-of-m-open"
                                values={{
                                    open: String(rollup.taskOpenCount),
                                    total: String(rollup.taskTotalCount),
                                    // only the open count is emphasised; the rest stays muted
                                    b: (chunks: ReactNode) => <span className="text-fg font-semibold">{chunks}</span>,
                                }}
                            />
                        </RouterLink>
                    ) : (
                        <p className="text-fg-secondary m-0 text-[13px] whitespace-nowrap">
                            {t('debriefing.columns.no-tasks')}
                        </p>
                    )}
                </SummaryRow>
            </div>
        </section>
    );
}

function ScoreAveragesCard({ rollup }: { rollup: DebriefingClientRollup }) {
    return (
        <section className={CARD_CLASS_NAME}>
            <CardTitle
                labelKey="debriefing.rollup.averages-title"
                subtitleKey="debriefing.rollup.averages-subtitle"
                tooltipKey="debriefing.rollup.averages-tooltip"
            />
            <div className="flex flex-col gap-2.25">
                {SCORE_ROWS.map(({ labelKey, field }) => {
                    const score = rollup[field] as DebriefingScoreAverage;
                    return (
                        <div key={field} className="flex items-center justify-between gap-3">
                            <p className="m-0 truncate text-[13px]">{t(labelKey)}</p>
                            {score.average != null ? (
                                <ScorePill average={score.average} />
                            ) : (
                                <p className="text-fg-secondary m-0 text-xs whitespace-nowrap">
                                    {t('debriefing.rollup.no-self-evaluations')}
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

/** Issues rank above strengths, each block by descending count — the recurring facts read top-down. */
export function toPatternRows(rollup: DebriefingClientRollup): PatternRow[] {
    const byCount = (a: DebriefingRecurringTag, b: DebriefingRecurringTag) => b.debriefingCount - a.debriefingCount;
    return [
        ...[...rollup.recurringIssues].sort(byCount).map((tag): PatternRow => ({ ...tag, kind: 'issue' })),
        ...[...rollup.recurringStrengths].sort(byCount).map((tag): PatternRow => ({ ...tag, kind: 'strength' })),
    ];
}

function PatternsCard({ rollup }: { rollup: DebriefingClientRollup }) {
    const { classifiers } = useClassifiers({ codes: [DEBRIEFING_LESSON_CATEGORY_CLASSIFIER] });
    const [showAll, setShowAll] = useState(false);
    const rows = useMemo(() => toPatternRows(rollup), [rollup]);
    const visibleRows = showAll ? rows : rows.slice(0, VISIBLE_PATTERN_ROWS);

    return (
        <section className={CARD_CLASS_NAME}>
            <CardTitle
                labelKey="debriefing.rollup.patterns-title"
                subtitleKey="debriefing.rollup.patterns-subtitle"
                tooltipKey="debriefing.rollup.patterns-tooltip"
            />
            {rows.length === 0 ? (
                <p className="text-fg-secondary m-0 text-[13px]">{t('debriefing.rollup.no-patterns')}</p>
            ) : null}
            <div className="flex flex-col">
                {visibleRows.map((row) => {
                    const isIssue = row.kind === 'issue';
                    const categoryName = getClassifierText(
                        classifiers?.[DEBRIEFING_LESSON_CATEGORY_CLASSIFIER],
                        row.categoryCode
                    );
                    return (
                        <div
                            key={`${row.kind}-${row.categoryCode}`}
                            className="border-divider flex items-center gap-2.5 border-b py-2.25 last:border-b-0"
                        >
                            <span
                                className={cn(
                                    'size-2 shrink-0 rounded-full',
                                    isIssue ? 'bg-fg-warning' : 'bg-fg-success'
                                )}
                            />
                            <Tooltip content={categoryName}>
                                <p className="m-0 flex-1 truncate text-[13px]">{categoryName}</p>
                            </Tooltip>
                            <p
                                className={cn(
                                    'm-0 flex-none text-xs font-semibold whitespace-nowrap',
                                    isIssue ? 'text-fg-warning' : 'text-fg-success'
                                )}
                            >
                                {t(isIssue ? 'debriefing.rollup.kind-issue' : 'debriefing.rollup.kind-strength')}
                            </p>
                            <p className="text-fg-secondary m-0 flex-none text-xs whitespace-nowrap">
                                {t('debriefing.rollup.n-debriefings', { count: String(row.debriefingCount) })}
                            </p>
                        </div>
                    );
                })}
            </div>
            {!showAll && rows.length > VISIBLE_PATTERN_ROWS ? (
                <button
                    type="button"
                    className="entity-link text-fg hover:text-link mt-2.5 w-fit cursor-pointer text-[13px] no-underline hover:underline"
                    onClick={() => setShowAll(true)}
                >
                    {t('debriefing.rollup.show-all-categories', { count: String(rows.length) })}
                </button>
            ) : null}
        </section>
    );
}

/**
 * Client-tab rollup: aggregates over the client's FULL debriefing set (deliberately not
 * access-filtered on the BE) — hence the includes-all disclaimer rendered by the parent view.
 * A client with no debriefings at all gets no panel, only the list's empty state.
 */
export function DebriefingClientRollupPanel({ clientPartyId }: { clientPartyId: number }) {
    const { data, isError, refetch } = useGetDebriefingClientRollupQuery({ clientPartyId });

    if (isError) {
        return (
            <Alert className={ALERT_CLASS_NAME}>
                <span className="flex w-6 shrink-0 justify-center">
                    <ErrorOutlineIcon className="text-error size-4" />
                </span>
                <div className="min-w-0">
                    <FormattedMessage id="debriefing.rollup.error" />
                </div>
                <div className="ml-auto shrink-0 pl-4">
                    <Button {...buttonStyle({ variant: 'text' }, 'text-fg')} onClick={() => void refetch()}>
                        <FormattedMessage id="debriefing.rollup.retry" />
                    </Button>
                </div>
            </Alert>
        );
    }
    if (!data || data.debriefingCount === 0) {
        return null;
    }

    return (
        <div>
            <p className="text-fg-secondary m-0 mb-3.5 flex items-center gap-1.5 text-xs">
                <InfoOutlinedIcon className="h-3.5 w-3.5" />
                {t('debriefing.rollup.includes-all-disclaimer')}
            </p>
            <div className="new:gap-(--card-gap) grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1fr)_minmax(0,1.4fr)]">
                <SummaryCard clientPartyId={clientPartyId} rollup={data} />
                <ScoreAveragesCard rollup={data} />
                <PatternsCard rollup={data} />
            </div>
        </div>
    );
}
